import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, RotateCcw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const PAGE = 10;

const SUPER_ONLY = ["name", "status", "deleted", "anlop_enabled", "pax_enabled"];

const StatusCell = ({ e, onRevert, own }) => {
  if (e.reverted) return <span className="text-xs text-slate-500" data-testid={`audit-${e.id}-reverted`}>Tilbakestilt av {e.reverted_by}</span>;
  if (!e.revertable || (own && SUPER_ONLY.includes(e.field))) return <span className="text-xs text-slate-400">–</span>;
  return (
    <Button size="sm" variant="outline" onClick={() => onRevert(e)} data-testid={`audit-${e.id}-revert`}>
      <Undo2 className="w-3.5 h-3.5 mr-1" />Angre
    </Button>
  );
};

export const AuditLog = ({ customers = [], refreshKey, onReverted, customerId }) => {
  const [cid, setCid] = useState(customerId || "all");
  const own = !!customerId;
  const base = own ? `/customers/${customerId}/audit` : "/admin/audit";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(total / PAGE));

  const load = useCallback(async () => {
    const { data } = await api.get(base, { params: { customer_id: own || cid === "all" ? undefined : cid, limit: PAGE, skip: page * PAGE } });
    setItems(data.items);
    setTotal(data.total);
  }, [base, own, cid, page]);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load, refreshKey]);

  const revert = async () => {
    try {
      const { data } = await api.post(`${base}/${confirm.id}/revert`);
      toast.success("Endringen er tilbakestilt");
      setConfirm(null);
      setPage(0);
      load();
      onReverted(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="audit-log">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors rounded-lg" data-testid="audit-toggle">
        <span className="flex items-baseline gap-2">
          <span className="font-display text-xl font-semibold text-slate-900">Endringslogg</span>
          <span className="text-sm text-slate-500" data-testid="audit-total">{total} endringer</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (<>
      <div className="flex items-center justify-between gap-3 p-4 border-y border-slate-100">
        {own ? <span className="text-sm text-slate-500">Alle endringer for kalkulatoren, nyeste først.</span> : (
        <Select value={cid} onValueChange={(v) => { setCid(v); setPage(0); }}>
          <SelectTrigger className="w-60" data-testid="audit-customer-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle kunder</SelectItem>
            {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>)}
        <Button variant="ghost" size="sm" onClick={() => load()} data-testid="audit-refresh"><RotateCcw className="w-4 h-4" /></Button>
      </div>
      <div className="overflow-x-auto">
        {own && (
          <ul className="sm:hidden divide-y divide-slate-100" data-testid="audit-mobile-list">
            {items.map((e) => (
              <li key={e.id} className={`p-4 space-y-1 ${e.reverted ? "opacity-60" : ""}`} data-testid={`audit-card-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-xs font-medium text-sky-800">{e.field_label}{e.is_revert && " · angret"}</span>
                  <StatusCell e={e} onRevert={setConfirm} own={own} />
                </div>
                <div className="text-sm text-slate-700 break-words">{e.summary}</div>
                <div className="text-xs text-slate-500 break-all">{fmtDate(e.ts)} · {e.actor_email}</div>
              </li>
            ))}
            {!items.length && <li className="py-10 text-center text-slate-500">Ingen endringer registrert ennå.</li>}
          </ul>
        )}
        <table className={`w-full text-left text-sm ${own ? "hidden sm:table min-w-[640px]" : "min-w-[820px]"}`}>
          <thead>
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2.5 px-4 font-medium">Tidspunkt</th><th className="py-2.5 px-4 font-medium">Hvem</th>
              {!own && <th className="py-2.5 px-4 font-medium">Kunde</th>}<th className="py-2.5 px-4 font-medium">Hva</th>
              <th className="py-2.5 px-4 font-medium text-right">Handling</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className={`border-t border-slate-100 align-top ${e.reverted ? "opacity-60" : ""}`} data-testid={`audit-row-${e.id}`}>
                <td className="py-3 px-4 whitespace-nowrap font-num text-xs text-slate-600">{fmtDate(e.ts)}</td>
                <td className="py-3 px-4">
                  <div className="text-slate-900">{e.actor_email}</div>
                  <div className="text-xs text-slate-500">{e.actor_role === "super_admin" ? "Super Admin" : "Kundeadmin"}</div>
                </td>
                {!own && <td className="py-3 px-4 text-slate-700">{e.customer_name || "–"}</td>}
                <td className="py-3 px-4">
                  <div className="text-xs font-medium text-sky-800">{e.field_label}{e.is_revert && " · angret"}</div>
                  <div className="text-slate-700 max-w-md break-words">{e.summary}</div>
                </td>
                <td className="py-3 px-4 text-right"><StatusCell e={e} onRevert={setConfirm} own={own} /></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={own ? 4 : 5} className="py-10 text-center text-slate-500">Ingen endringer registrert ennå.</td></tr>}
          </tbody>
        </table>
      </div>
      {total > PAGE && (
        <div className="flex items-center justify-between gap-3 p-3 border-t border-slate-100">
          <span className="text-sm text-slate-500" data-testid="audit-page-info">Side {page + 1} av {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4 mr-1" />Forrige</Button>
            <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} data-testid="audit-next-page">Neste<ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}
      </>)}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Angre denne endringen?</AlertDialogTitle>
            <AlertDialogDescription>
              «{confirm?.field_label}»{own ? "" : ` for ${confirm?.customer_name}`} settes tilbake slik det var før endringen. Senere endringer i samme felt overskrives. Kalkulatoren oppdateres med en gang.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={revert} data-testid="audit-revert-confirm">Angre endring</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
